import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Users, Calendar, CheckCircle, Clock, AlertCircle, Eye, Edit, Send, Trash2, Search, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import PaginationControls from './PaginationControls';
import Toast from './Toast';
import SurveyPreview from './SurveyPreview';
import SurveyDistributionPreview from './SurveyDistributionPreview';
import * as surveyAPI from '../../services/surveyAPI';
import './SurveyListGrouped.css';

const SurveyListGrouped = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [filteredCohorts, setFilteredCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCohorts, setExpandedCohorts] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('active,draft');
  const [sortBy, setSortBy] = useState('name-asc');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [distributionModal, setDistributionModal] = useState(null);
  const [currentCohortId, setCurrentCohortId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  const ITEMS_PER_PAGE = 15;

  useEffect(() => {
    surveyAPI.setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  useEffect(() => {
    loadGroupedSurveys();
  }, [statusFilter]);

  // Apply client-side filtering and sorting whenever cohorts or filters change
  useEffect(() => {
    let processed = [...cohorts];
    
    // Apply filters
    processed = applyFilters(processed);
    
    // Apply sorting
    processed = applySorting(processed);
    
    setFilteredCohorts(processed);
    setCurrentPage(1); // Reset to first page when filters change
  }, [cohorts, searchQuery, startDateFilter, endDateFilter, sortBy]);

  // Handle expandCohort query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const expandCohortId = params.get('expandCohort');
    if (expandCohortId) {
      const cohortId = parseInt(expandCohortId);
      setExpandedCohorts(new Set([cohortId]));
      // Scroll to the cohort after a brief delay to ensure it's rendered
      setTimeout(() => {
        const element = document.getElementById(`cohort-${cohortId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [location.search]);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const loadGroupedSurveys = async () => {
    try {
      setLoading(true);
      // Load ALL cohorts (no pagination limit) so search works across all data
      const response = await surveyAPI.getSurveysGroupedByCohort({
        status: statusFilter,
        page: 1,
        limit: 1000 // Load all cohorts
      });
      
      const cohortsList = response.data?.cohorts || response.cohorts || [];
      
      setCohorts(cohortsList);
    } catch (err) {
      console.error('Error loading grouped surveys:', err);
      showToast('Failed to load surveys', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (cohortsList) => {
    let filtered = [...cohortsList];
    
    // Filter by search query (case-insensitive)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(cohort => 
        cohort.cohort_name.toLowerCase().includes(query)
      );
    }
    
    // Filter by start date
    if (startDateFilter) {
      filtered = filtered.filter(cohort => {
        if (!cohort.start_date) return false;
        return new Date(cohort.start_date) >= new Date(startDateFilter);
      });
    }
    
    // Filter by end date
    if (endDateFilter) {
      filtered = filtered.filter(cohort => {
        if (!cohort.end_date) return false;
        return new Date(cohort.end_date) <= new Date(endDateFilter);
      });
    }
    
    return filtered;
  };

  const applySorting = (cohortsList) => {
    const sorted = [...cohortsList];
    
    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => 
          a.cohort_name.localeCompare(b.cohort_name)
        );
      case 'name-desc':
        return sorted.sort((a, b) => 
          b.cohort_name.localeCompare(a.cohort_name)
        );
      case 'start-newest':
        return sorted.sort((a, b) => 
          new Date(b.start_date || 0) - new Date(a.start_date || 0)
        );
      case 'start-oldest':
        return sorted.sort((a, b) => 
          new Date(a.start_date || 0) - new Date(b.start_date || 0)
        );
      case 'completion-high':
        return sorted.sort((a, b) => 
          (b.overall_progress?.overall_completion || 0) - (a.overall_progress?.overall_completion || 0)
        );
      case 'completion-low':
        return sorted.sort((a, b) => 
          (a.overall_progress?.overall_completion || 0) - (b.overall_progress?.overall_completion || 0)
        );
      case 'participants-high':
        return sorted.sort((a, b) => 
          (b.participant_count || 0) - (a.participant_count || 0)
        );
      case 'participants-low':
        return sorted.sort((a, b) => 
          (a.participant_count || 0) - (b.participant_count || 0)
        );
      default:
        return sorted;
    }
  };

  const toggleCohortExpansion = (cohortId) => {
    const newExpanded = new Set(expandedCohorts);
    if (newExpanded.has(cohortId)) {
      newExpanded.delete(cohortId);
    } else {
      newExpanded.add(cohortId);
    }
    setExpandedCohorts(newExpanded);
  };

  const getSurveyStatusIcon = (survey) => {
    if (!survey) return <Clock className="survey-status-icon pending" />;
    
    switch (survey.status) {
      case 'COMPLETED':
        return <CheckCircle className="survey-status-icon completed" />;
      case 'ACTIVE':
        return <AlertCircle className="survey-status-icon active" />;
      case 'DRAFT':
        return <Clock className="survey-status-icon draft" />;
      default:
        return <Clock className="survey-status-icon pending" />;
    }
  };

  const getSurveyStatusText = (survey) => {
    if (!survey) return 'Not Created';
    
    switch (survey.status) {
      case 'COMPLETED':
        return 'Completed';
      case 'ACTIVE':
        return `${survey.completion_percentage}% Complete`;
      case 'DRAFT':
        return 'Draft';
      default:
        return 'Unknown';
    }
  };

  const getOverallStatusColor = (progress) => {
    if (progress.overall_completion >= 90) return 'excellent';
    if (progress.overall_completion >= 70) return 'good';
    if (progress.overall_completion >= 40) return 'fair';
    return 'poor';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="survey-grouped-loading">
          <div className="loading-spinner"></div>
          <p>Loading surveys...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="survey-grouped-container">
        <div className="survey-grouped-header">
          <h1>Surveys by Cohort</h1>
          <p>View and manage surveys organized by cohort with completion tracking</p>
        </div>

        {/* Search Bar */}
        <div className="search-bar-container">
          <div className="search-input-wrapper">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search cohorts by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Filters & Sorting */}
        <div className="survey-grouped-filters">
          <div className="filter-group">
            <label htmlFor="status-filter">Status:</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="active,draft">Active & Draft</option>
              <option value="active">Active Only</option>
              <option value="draft">Draft Only</option>
              <option value="completed">Completed</option>
              <option value="all">All Cohorts</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="sort-by">Sort By:</label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="start-newest">Start Date (Newest)</option>
              <option value="start-oldest">Start Date (Oldest)</option>
              <option value="completion-high">Completion (High-Low)</option>
              <option value="completion-low">Completion (Low-High)</option>
              <option value="participants-high">Participants (Most)</option>
              <option value="participants-low">Participants (Least)</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="start-date-filter">Start After:</label>
            <input
              type="date"
              id="start-date-filter"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              className="filter-date-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date-filter">End Before:</label>
            <input
              type="date"
              id="end-date-filter"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              className="filter-date-input"
            />
          </div>

          {(startDateFilter || endDateFilter) && (
            <button
              className="clear-filters-btn"
              onClick={() => {
                setStartDateFilter('');
                setEndDateFilter('');
              }}
              title="Clear date filters"
            >
              Clear Dates
            </button>
          )}
        </div>

        {/* Cohort List */}
        <div className="cohort-list">
          {filteredCohorts.length === 0 ? (
            <div className="empty-state">
              <h3>No cohorts found</h3>
              <p>No cohorts match the current filter criteria.</p>
            </div>
          ) : (() => {
            // Calculate pagination
            const totalPages = Math.ceil(filteredCohorts.length / ITEMS_PER_PAGE);
            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            const paginatedCohorts = filteredCohorts.slice(startIndex, endIndex);
            
            return (
              <>
            {paginatedCohorts.map(cohort => {
              const isExpanded = expandedCohorts.has(cohort.cohort_id);
              const overallStatus = getOverallStatusColor(cohort.overall_progress);
              
              return (
                <div key={cohort.cohort_id} id={`cohort-${cohort.cohort_id}`} className="cohort-card">
                  {/* Cohort Header */}
                  <div 
                    className="cohort-header"
                    onClick={() => toggleCohortExpansion(cohort.cohort_id)}
                  >
                    <div className="cohort-header-left">
                      <div className="expand-icon">
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </div>
                      <div className="cohort-info">
                        <h3 className="cohort-name">{cohort.cohort_name}</h3>
                        <div className="cohort-meta">
                          <span className={`cohort-status ${cohort.cohort_status.toLowerCase()}`}>
                            {cohort.cohort_status}
                          </span>
                          <span className="cohort-dates">
                            <Calendar size={14} />
                            {formatDate(cohort.start_date)} - {formatDate(cohort.end_date)}
                          </span>
                          <span className="participant-count">
                            <Users size={14} />
                            {cohort.participant_count} participants
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="cohort-header-right">
                      <div className="overall-progress">
                        <div className="progress-text">
                          <span className="progress-label">Overall Progress</span>
                          <span className={`progress-value ${overallStatus}`}>
                            {cohort.overall_progress.overall_completion.toFixed(1)}%
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className={`progress-fill ${overallStatus}`}
                            style={{ width: `${cohort.overall_progress.overall_completion}%` }}
                          ></div>
                        </div>
                        <div className="surveys-sent">
                          {cohort.overall_progress.surveys_sent} of {cohort.overall_progress.surveys_total} surveys sent
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="cohort-content">
                      <div className="surveys-grid">
                        {['BASELINE', 'MIDPOINT', 'FINAL'].map(type => {
                          const survey = cohort.surveys[type];
                          
                          return (
                            <div key={type} className="survey-card">
                              <div className="survey-header">
                                <div className="survey-type">
                                  {getSurveyStatusIcon(survey)}
                                  <span className="survey-type-name">{type}</span>
                                </div>
                                <div className="survey-status">
                                  {getSurveyStatusText(survey)}
                                </div>
                              </div>
                              
                              {survey ? (
                                <div className="survey-details">
                                  <h4 className="survey-name">{survey.name}</h4>
                                  
                                  <div className="survey-stats">
                                    <div className="stat">
                                      <span className="stat-label">Completion</span>
                                      <span className="stat-value">
                                        {survey.completion_percentage}%
                                      </span>
                                    </div>
                                    <div className="stat">
                                      <span className="stat-label">Participants</span>
                                      <span className="stat-value">
                                        {survey.completed_participants}/{survey.total_participants}
                                      </span>
                                    </div>
                                    <div className="stat">
                                      <span className="stat-label">Responses</span>
                                      <span className="stat-value">
                                        {survey.completed_instances}/{survey.total_instances}
                                      </span>
                                    </div>
                                  </div>

                                  {survey.completion_percentage > 0 && (
                                    <div className="completion-bar">
                                      <div 
                                        className="completion-fill"
                                        style={{ width: `${survey.completion_percentage}%` }}
                                      ></div>
                                    </div>
                                  )}

                                  {survey.sent_date && (
                                    <div className="survey-sent-date">
                                      Sent: {formatDate(survey.sent_date)}
                                    </div>
                                  )}

                                  {/* Recipient Breakdown */}
                                  {survey.recipient_breakdown && Object.keys(survey.recipient_breakdown).length > 0 && (
                                    <div className="recipient-breakdown">
                                      <h5>Response Breakdown</h5>
                                      <div className="recipient-stats">
                                        {Object.entries(survey.recipient_breakdown).map(([type, stats]) => (
                                          <div key={type} className="recipient-stat">
                                            <span className="recipient-type">
                                              {type.replace('_', ' ')}
                                            </span>
                                            <span className="recipient-progress">
                                              {stats.completed}/{stats.total} ({stats.percentage}%)
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Action Buttons */}
                                  <div className="survey-actions">
                                    <button
                                      className="action-btn preview-btn"
                                      onClick={() => {
                                        setCurrentCohortId(cohort.cohort_id);
                                        setPreviewModal(survey);
                                      }}
                                      title="Preview Survey"
                                    >
                                      <Eye size={14} />
                                      <span>Preview</span>
                                    </button>
                                    <button
                                      className="action-btn edit-btn"
                                      onClick={() => {
                                        // Add returnTo parameter so edit returns to grouped view
                                        navigate(`/leading/admin/surveys/edit/${survey.id}?returnTo=grouped&expandCohort=${cohort.cohort_id}`);
                                      }}
                                      title="Edit Survey"
                                    >
                                      <Edit size={14} />
                                      <span>Edit</span>
                                    </button>
                                    {survey.status === 'DRAFT' && (
                                      <button
                                        className="action-btn send-btn"
                                        onClick={() => {
                                          setCurrentCohortId(cohort.cohort_id);
                                          setDistributionModal(survey);
                                        }}
                                        title="Send Survey"
                                      >
                                        <Send size={14} />
                                        <span>Send</span>
                                      </button>
                                    )}
                                    <button
                                      className="action-btn delete-btn"
                                      onClick={async () => {
                                        if (window.confirm(`Are you sure you want to delete "${survey.name}"?`)) {
                                          try {
                                            await surveyAPI.deleteSurvey(survey.id);
                                            showToast('Survey deleted successfully', 'success');
                                            loadGroupedSurveys();
                                          } catch (error) {
                                            console.error('Error deleting survey:', error);
                                            showToast('Failed to delete survey', 'error');
                                          }
                                        }
                                      }}
                                      title="Delete Survey"
                                    >
                                      <Trash2 size={14} />
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="survey-placeholder">
                                  <p>Survey not yet created</p>
                                  <button 
                                    className="create-survey-btn"
                                    onClick={() => {
                                      // Determine which survey to copy from
                                      let copyFrom = null;
                                      if (type === 'MIDPOINT' && cohort.surveys.BASELINE?.id) {
                                        copyFrom = 'BASELINE';
                                      } else if (type === 'FINAL') {
                                        if (cohort.surveys.MIDPOINT?.id) {
                                          copyFrom = 'MIDPOINT';
                                        } else if (cohort.surveys.BASELINE?.id) {
                                          copyFrom = 'BASELINE';
                                        }
                                      }
                                      
                                      // Build URL with query parameters
                                      const params = new URLSearchParams({
                                        cohortId: cohort.cohort_id,
                                        surveyType: type
                                      });
                                      
                                      if (copyFrom) {
                                        params.append('copyFrom', copyFrom);
                                      }
                                      
                                      // Add returnTo parameter so we know to come back here
                                      params.append('returnTo', 'grouped');
                                      navigate(`/leading/admin/surveys/new?${params.toString()}`);
                                    }}
                                  >
                                    Create {type} Survey
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                total={filteredCohorts.length}
                limit={ITEMS_PER_PAGE}
                hasNext={currentPage < totalPages}
                hasPrev={currentPage > 1}
                onPageChange={(newPage) => {
                  setCurrentPage(newPage);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                itemType="cohorts"
              />
            )}
              </>
            );
          })()}
        </div>

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Preview Modal */}
        {previewModal && (
          <SurveyPreview
            survey={previewModal}
            onClose={() => {
              setPreviewModal(null);
              setCurrentCohortId(null);
            }}
            onSend={() => {
              setDistributionModal(previewModal);
              setPreviewModal(null);
            }}
          />
        )}

        {/* Distribution Modal */}
        {distributionModal && (
          <SurveyDistributionPreview
            survey={distributionModal}
            onClose={() => {
              setDistributionModal(null);
              setCurrentCohortId(null);
            }}
            onConfirm={() => {
              setDistributionModal(null);
              showToast('Survey sent successfully!');
              // Reload surveys and keep cohort expanded
              loadGroupedSurveys();
              // Re-expand the cohort after reload
              if (currentCohortId) {
                setTimeout(() => {
                  setExpandedCohorts(new Set([currentCohortId]));
                  const element = document.getElementById(`cohort-${currentCohortId}`);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 500);
              }
              setCurrentCohortId(null);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default SurveyListGrouped;
